import { useEffect, useState, useCallback } from "react";
import { X } from "lucide-react";
import { supabase } from "../lib/supabase";
import { useUser } from "../context/UserContext";
import { useNavigate } from "react-router-dom";
import PostViewerModal from "./PostViewerModal";

function NotificationsSidebar({ open, onClose }) {
    const { profile, user } = useUser();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [notifications, setNotifications] = useState([]);

    // PostViewerModal for clicking a post thumbnail
    const [selectedPost, setSelectedPost] = useState(null);
    const [selectedPostProfile, setSelectedPostProfile] = useState(null);

    // ── GROUP ────────────────────────────────────────────────────────────────
    const groupNotifications = (list) => {
        const groups = { Today: [], Yesterday: [], "This Week": [], "This Month": [], Older: [] };
        const now = new Date();
        list.forEach((n) => {
            const diffDays = Math.floor((now - new Date(n.created_at)) / 86_400_000);
            if (diffDays === 0)       groups.Today.push(n);
            else if (diffDays === 1)  groups.Yesterday.push(n);
            else if (diffDays <= 7)   groups["This Week"].push(n);
            else if (diffDays <= 30)  groups["This Month"].push(n);
            else                       groups.Older.push(n);
        });
        return groups;
    };

    // ── FETCH ────────────────────────────────────────────────────────────────
    const fetchNotifications = useCallback(async () => {
        if (!profile?.id) return;
        try {
            setLoading(true);
            const { data, error } = await supabase
                .from("notifications")
                .select(`
                    id, user_id, actor_id, post_id, type, message, is_read, created_at,
                    actor:profiles!notifications_actor_id_profiles_fkey(username, avatar_url)
                `)
                .eq("user_id", profile.id)
                .order("created_at", { ascending: false })
                .limit(100);

            if (error) throw error;
            setNotifications(data || []);

            // Mark all as read
            await supabase
                .from("notifications")
                .update({ is_read: true })
                .eq("user_id", profile.id)
                .eq("is_read", false);
        } catch (err) {
            console.error("Failed to fetch notifications:", err);
        } finally {
            setLoading(false);
        }
    }, [profile]);

    useEffect(() => {
        if (open) fetchNotifications();
    }, [open, fetchNotifications]);

    // ── OPEN POST from notification ───────────────────────────────────────────
    const openPost = async (postId) => {
        if (!postId) return;

        const { data, error } = await supabase
            .from("posts")
            .select(`
                id, caption, created_at, hide_like_counts, turn_off_commenting, user_id,
                profiles:user_id(id, username, avatar_url),
                post_images(id, image_path, order_index),
                likes(user_id)
            `)
            .eq("id", postId)
            .single();

        if (error || !data) return;

        // Sign image URLs
        const sorted = (data.post_images || []).sort((a, b) => a.order_index - b.order_index);
        const withUrls = await Promise.all(sorted.map(async (img) => {
            const { data: sd } = await supabase.storage
                .from("posts").createSignedUrl(img.image_path, 3600);
            return { ...img, signedUrl: sd?.signedUrl || null };
        }));

        const formatted = {
            ...data,
            post_images: withUrls,
            likesCount: data.likes?.length || 0,
            isLiked: data.likes?.some(l => l.user_id === user?.id) || false,
        };

        setSelectedPost(formatted);
        setSelectedPostProfile(data.profiles);
    };

    if (!open) return null;

    const grouped = groupNotifications(notifications);

    const formatTime = (dateStr) => {
        const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
        if (diff < 60) return `${diff}s`;
        if (diff < 3600) return `${Math.floor(diff / 60)}m`;
        if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
        if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
        return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric" });
    };

    return (
        <>
            {/* Overlay */}
            <div className="notif-overlay" onClick={onClose} />

            {/* Sidebar */}
            <aside className="notif-sidebar">

                {/* Header */}
                <div className="notif-header">
                    <h2>Notifications</h2>
                    <button className="notif-close-btn" onClick={onClose}>
                        <X size={20} strokeWidth={1.5} />
                    </button>
                </div>

                {/* Content */}
                <div className="notif-content">

                    {/* Loading Skeleton */}
                    {loading && (
                        <div className="notif-skeleton-wrapper">
                            {Array.from({ length: 8 }).map((_, i) => (
                                <div key={i} className="notif-skeleton-item">
                                    <div className="notif-skeleton-avatar shimmer" />
                                    <div className="notif-skeleton-text">
                                        <div className="notif-skeleton-line shimmer" />
                                        <div className="notif-skeleton-line small shimmer" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Empty */}
                    {!loading && notifications.length === 0 && (
                        <div className="notif-empty">
                            <p className="notif-empty-title">No notifications yet</p>
                            <p className="notif-empty-subtitle">
                                When someone likes, comments, or follows you, you'll see it here.
                            </p>
                        </div>
                    )}

                    {/* Groups */}
                    {!loading && notifications.length > 0 &&
                        Object.entries(grouped).map(([title, items]) => {
                            if (items.length === 0) return null;
                            return (
                                <div key={title} className="notif-group">
                                    <p className="notif-group-title">{title}</p>

                                    {items.map((n) => (
                                        <div
                                            key={n.id}
                                            className={`notif-item ${n.is_read ? "" : "unread"} notif-clickable`}
                                            onClick={() => {
                                                if (n.type === "follow") {
                                                    onClose();
                                                    navigate(`/user/${encodeURIComponent(n.actor?.username)}`);
                                                } else if ((n.type === "like" || n.type === "comment") && n.post_id) {
                                                    openPost(n.post_id);
                                                }
                                            }}
                                        >
                                            {/* Avatar */}
                                            {n.actor?.avatar_url ? (
                                                <img
                                                    src={n.actor.avatar_url}
                                                    alt=""
                                                    className="notif-avatar-img"
                                                />
                                            ) : (
                                                <div className="notif-avatar notif-avatar-fallback">
                                                    {n.actor?.username?.charAt(0).toUpperCase() || "?"}
                                                </div>
                                            )}

                                            {/* Text */}
                                            <div className="notif-text">
                                                <p className="notif-message">
                                                    <span className="notif-username">
                                                        {n.actor?.username ? `${n.actor.username} ` : "Someone "}
                                                    </span>
                                                    {n.message}
                                                </p>
                                                <p className="notif-time">{formatTime(n.created_at)}</p>
                                            </div>

                                            {/* Post thumbnail — display only, not clickable */}
                                            {(n.type === "like" || n.type === "comment") && n.post_id && (
                                                <NotifPostThumb postId={n.post_id} />
                                            )}
                                        </div>
                                    ))}
                                </div>
                            );
                        })
                    }
                </div>
            </aside>

            {/* Post Viewer Modal */}
            {selectedPost && (
                <PostViewerModal
                    open={!!selectedPost}
                    onClose={() => { setSelectedPost(null); setSelectedPostProfile(null); }}
                    post={selectedPost}
                    posts={[selectedPost]}
                    profile={selectedPostProfile}
                    onNextPost={() => {}}
                    onPrevPost={() => {}}
                    onPostDeleted={() => { setSelectedPost(null); }}
                    onPostUpdated={(postId, updatedData) => {
                        setSelectedPost(prev => prev?.id === postId ? { ...prev, ...updatedData } : prev);
                    }}
                />
            )}
        </>
    );
}

// ── POST THUMBNAIL (display only) ────────────────────────────────────────────
function NotifPostThumb({ postId }) {
    const [thumbUrl, setThumbUrl] = useState(null);

    useEffect(() => {
        if (!postId) return;
        const fetch = async () => {
            const { data } = await supabase
                .from("post_images")
                .select("image_path, order_index")
                .eq("post_id", postId)
                .order("order_index", { ascending: true })
                .limit(1)
                .single();

            if (data?.image_path) {
                const { data: sd } = await supabase.storage
                    .from("posts").createSignedUrl(data.image_path, 3600);
                if (sd?.signedUrl) setThumbUrl(sd.signedUrl);
            }
        };
        fetch();
    }, [postId]);

    if (!thumbUrl) return <div className="notif-post-thumb-skeleton" />;

    return (
        <div className="notif-post-thumb">
            <img src={thumbUrl} alt="" />
        </div>
    );
}

export default NotificationsSidebar;
